import { Global } from '@emotion/react'
import { styled } from '@mui/material/styles'
import * as React from 'react'

import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { ButtonBase, Chip, Divider, Stack } from '@mui/material'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import Typography from '@mui/material/Typography'
import { grey } from '@mui/material/colors'
import HereNativeRouteMap, {
  HereMarker,
} from 'components/Common/HereMaps/HereNativeRouteMap'
import { geoCentre } from 'helpers/geoHelpers'
import NumberPlate from './NumberPlate'
import StatusCompleted from './StatusCompleted'
import StatusInProgress from './StatusInProgress'
import StatusOnRoute from './StatusOnRoute'
import StatusOther from './StatusOther'

const Root = styled('div')(() => ({
  height: '100%',
  backgroundColor: grey[100],
}))

const StyledBox = styled('div')(() => ({
  backgroundColor: '#fff',
}))

interface RouteInfo {
  estimatedJourneyDistance: number
  estimatedJourneyTime: number
  directionsResponse: object
}
interface Props {
  drawerBleeding: number
  drawerHeight: number
  type: string
  tracking: any
  progress: string
  eta?: number
  setEta: React.Dispatch<React.SetStateAction<number | undefined>>
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  data?: any
}

export default function SwipeableEdgeDrawer(props: Props) {
  const {
    type,
    tracking,
    progress,
    data,
    drawerBleeding,
    drawerHeight,
    eta,
    setEta,
    open,
    setOpen,
  } = props

  const journeyStatus = React.useMemo(() => {
    switch (String(progress)) {
      case 'Confirmed':
        return 'Looking for a driver'
      case 'Driver_Allocated':
        return 'Driver has been allocated'
      case 'OnRoute':
        return 'Driver is on the way'
      case 'Arrived':
        return 'Your driver is waiting for you'
      case 'Passenger_Picked_Up':
      case 'OnRoute_Passenger_On_Board':
      case 'Passenger_Dropped_Off':
        return "You're on your way..."
      case 'Supplier_Cancelled':
      case 'Cancelled':
        return 'Booking Cancelled'
      case 'Manual_Intervention_Required':
        return "We're looking for car for you..."
      case 'Completed':
      case 'Completed_Pending_Invoice':
        return "You've Arrived!"
      case 'No_Show':
        return 'Passenger - No Show' //TODO: Better language
      default:
        return 'Awaiting allocation'
    }
  }, [progress])

  const Puller = ({
    open,
    onClick,
  }: {
    open: boolean
    onClick: () => void
  }) => (
    <ButtonBase
      sx={{
        width: '100%',
        height: 20,
        backgroundColor: grey[100],
        borderRadius: 3,
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        cursor: 'pointer',
      }}
      onClick={onClick}
      disableRipple
    >
      {open ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
    </ButtonBase>
  )
  const toNumber = (v: any) => {
    if (v == null) return undefined
    const n = typeof v === 'string' ? parseFloat(v) : v
    return Number.isFinite(n) ? (n as number) : undefined
  }

  const toLatLng = (v: any) => {
    if (!v) return undefined
    const lat = toNumber(v.lat ?? v.latitude)
    const lng = toNumber(v.lng ?? v.longitude)
    if (lat == null || lng == null) return undefined
    return { lat, lng }
  }

  const updateRouteInfo = (routeInfo: RouteInfo) => {
    console.log('routeInfo', routeInfo)
    setEta(routeInfo?.estimatedJourneyTime)
  }

  const toggleDrawer = (newOpen: boolean) => () => {
    console.log(newOpen, open, drawerBleeding)
    setOpen(newOpen)
  }

  const handleRef = React.useRef<HTMLDivElement | null>(null)
  const [contentOffsetPx, setContentOffsetPx] = React.useState(0)

  React.useLayoutEffect(() => {
    const el = handleRef.current
    if (!el) return
    const update = () => {
      const handleH = el.offsetHeight || 0
      const overlap = Math.max(0, handleH - drawerBleeding)
      setContentOffsetPx(overlap)
      console.log('offsetHight', el.offsetHeight, overlap, handleH)
    }

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    window.addEventListener('resize', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [drawerBleeding])

  const renderMarkers = (
    type: string,
    origin?: LatLngLiteral,
    destination?: LatLngLiteral,
    additionalStops: LatLngLiteral[] = [],
  ): HereMarker[] => {
    switch (String(progress)) {
      case 'Driver_Allocated':
      case 'Arrived':
      case 'OnRoute':
        return [
          ...(origin ? [{ position: origin, kind: 'tracking' as const }] : []),
          ...(destination
            ? [{ position: destination, kind: 'origin' as const, label: 'A' }]
            : []),
        ]

      case 'Passenger_Picked_Up':
      case 'In_Progress':
      case 'Passenger_Dropped_Off':
      case 'OnRoute_Passenger_On_Board':
        return [
          ...(origin ? [{ position: origin, kind: 'tracking' as const }] : []),
          ...(destination
            ? [
                {
                  position: destination,
                  kind: 'destination' as const,
                  label: 'B',
                },
              ]
            : []),
          ...additionalStops.map((stop, idx) => ({
            key: `stop-${idx}`,
            position: stop,
            kind: 'stop' as const,
            label: idx + 1,
          })),
        ]
      case 'Confirmed':
      case 'Completed':
      case 'Completed_Pending_Invoice':
        return [
          ...(origin ? [{ position: origin, kind: 'origin' as const }] : []),
          ...(destination
            ? [{ position: destination, kind: 'destination' as const }]
            : []),
          ...additionalStops.map((stop, idx) => ({
            key: `stop-${idx}`,
            position: stop,
            kind: 'stop' as const,
            label: idx + 1,
          })),
        ]

      default:
        return [
          ...(origin ? [{ position: origin, kind: 'origin' as const }] : []),
          ...(destination
            ? [{ position: destination, kind: 'destination' as const }]
            : []),
          ...additionalStops.map((stop, idx) => ({
            key: `stop-${idx}`,
            position: stop,
            kind: 'stop' as const,
            label: idx + 1,
          })),
        ]
    }
  }

  const mapProps = () => {
    const toLatLngLiteral = (c: any) =>
      c && typeof c.lat === 'number' && typeof c.lon === 'number'
        ? { lat: c.lat, lng: c.lon }
        : undefined

    switch (String(progress)) {
      case 'Driver_Allocated':
      case 'OnRoute':
        return {
          origin: tracking?.where,
          destination: data?.pickup,
          updateRouteInfo: updateRouteInfo,
          defaultZoom: 16,
          defaultCenter:
            toLatLngLiteral(data?.pickup) ?? toLatLngLiteral(data?.pickup),
          mapOptions: {
            zoomControl: true,
          },
        }
      case 'Arrived':
      case 'Passenger_Picked_Up':
      case 'In_Progress':
      case 'OnRoute_Passenger_On_Board':
        return {
          origin: tracking?.where,
          destination: data?.dropOff,
          defaultZoom: 16,
          defaultCenter: toLatLngLiteral(
            geoCentre([data?.pickup, data?.dropOff, data?.additionalStops]),
          ),
          updateRouteInfo: updateRouteInfo,
          additionalStops: data?.additionalStops,
          mapOptions: {
            zoomControl: true,
          },
        }
      case 'Confirmed':
      case 'Completed':
      case 'Completed_Pending_Invoice':
        return {
          origin: data?.pickup,
          destination: data?.dropOff,
          additionalStops: data?.additionalStops,
          defaultZoom: 16,
          defaultCenter: toLatLngLiteral(
            geoCentre([data?.pickup, data?.dropOff, data?.additionalStops]),
          ),
          mapOptions: {
            zoomControl: true,
          },
        }
      default:
        return {
          origin: data?.pickup,
          destination: data?.dropOff,
          additionalStops: data?.additionalStops,
          defaultZoom: 16,
          defaultCenter: toLatLngLiteral(
            geoCentre([data?.pickup, data?.dropOff]),
          ),
          mapOptions: {
            zoomControl: true,
          },
        }
    }
  }

  const StatusBody = () => {
    switch (progress) {
      case 'Driver_Allocated':
      case 'Arrived':
      case 'OnRoute':
        return <StatusOnRoute data={data} />
      case 'Passenger_Picked_Up':
      case 'In_Progress':
      case 'OnRoute_Passenger_On_Board':
      case 'Passenger_Dropped_Off':
        return <StatusInProgress data={data} />
      case 'Completed':
      case 'Completed_Pending_Invoice':
        return <StatusCompleted data={data} />
      case 'Supplier_Cancelled':
      case 'Manual_Intervention_Required':
      default:
        return <StatusOther data={data} />
    }
  }

  const DrawerHandle: React.FC<{ eta: number | undefined; tracking: any }> = ({
    eta,
    tracking,
  }) => {
    const vehicle = tracking?.driver?.vehicle

    return (
      <Box
        component="section"
        sx={{
          display: 'grid',
          gap: '0',
          minWidth: '100%',
        }}
      >
        <Box
          sx={{
            minWidth: '100%',
            fontSize: '16px',
            lineHeight: '2',
            paddingTop: '16px',
          }}
        >
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem />}
            spacing={2}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyItems: 'center',
                gap: '5px',
              }}
            >
              <Chip
                label={`${journeyStatus}`}
                sx={{ borderColor: tracking.ragStatus }}
                variant="outlined"
                size="small"
              />
              {typeof eta === 'number' &&
                ['OnRoute', 'Driver_Allocated', 'Passenger_Picked_Up'].includes(
                  progress,
                ) && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '5px',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography fontSize={'14px'}>
                      {eta >= 1 ? `ETA: ${eta}mins` : 'ETA: Now'}
                    </Typography>
                  </Box>
                )}
            </Box>

            {vehicle &&
              [
                'Driver_Waiting',
                'OnRoute',
                'Arrived',
                'Driver_Allocated',
              ].includes(progress) && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    fontSize: '14px',
                    gap: 0,
                    p: 0,
                    m: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '3px',
                      justifyContent: 'space-evenly',
                      padding: 0,
                      m: 0,
                    }}
                  >
                    {vehicle.colour && (
                      <Box>{vehicle.colour.toUpperCase()}</Box>
                    )}
                    {vehicle.makeAndModel && <Box>{vehicle.makeAndModel}</Box>}
                  </Box>

                  <NumberPlate
                    chars={vehicle.registrationNumber}
                    tz={data?.timeZone}
                  />
                </Box>
              )}
          </Stack>
        </Box>
      </Box>
    )
  }

  type LatLngLiteral = { lat: number; lng: number }

  console.log('drawerHeight', drawerHeight)
  return (
    <Root>
      <CssBaseline />
      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            height: `calc(${drawerHeight}% - ${drawerBleeding}px)`,
            overflow: 'visible',
          },
        }}
      />
      <Box sx={{ textAlign: 'center' }}>
        {(() => {
          const mp = { ...mapProps() }
          const origin = toLatLng((mp as any).origin)
          const destination = toLatLng((mp as any).destination)
          const additionalStops: LatLngLiteral[] = [
            ...(((mp as any).additionalStops ?? []) as any[])
              .map((s) => toLatLng(s))
              .filter(Boolean),
          ] as LatLngLiteral[]

          return (
            <HereNativeRouteMap
              {...mp}
              origin={origin}
              destination={destination}
              additionalStops={additionalStops}
              markers={renderMarkers(
                type,
                origin,
                destination,
                additionalStops,
              )}
              showRouteMarkers={false}
              mapWidth="100vw"
              mapHeight={open ? `${100 - Number(drawerHeight)}dvh` : '87dvh'}
            />
          )
        })()}
      </Box>
      <SwipeableDrawer
        container={null}
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        onClick={() => setOpen(!open)}
        swipeAreaWidth={drawerBleeding}
        sx={{ zIndex: 9999 }}
        disableSwipeToOpen={false}
        keepMounted
      >
        <StyledBox
          id="handle"
          sx={{
            position: 'absolute',
            top: -props.drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: 'visible',
            height: `${drawerBleeding + 1}px`,
            right: 0,
            left: 0,
          }}
        >
          <Puller open={open} onClick={() => setOpen(true)} />
          <Typography sx={{ p: 2, color: 'text.secondary' }}>
            <DrawerHandle eta={eta} tracking={tracking} />
          </Typography>
        </StyledBox>
        <StyledBox
          id="content"
          sx={{
            p: 2,
            height: '100%',
            overflow: 'auto',
            marginTop: `${contentOffsetPx}px`,
          }}
        >
          <StatusBody />
        </StyledBox>
      </SwipeableDrawer>
    </Root>
  )
}
